import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { buscarEmpresasReceita, criarReceitaManual } from "./api";
import type {
  EmpresaReceita,
  ReceitaManualIn,
  ReceitaManualOut,
} from "./tipos";

/*
 * Hooks de server-state da receita manual (Tela 5 §10.5 — ADR 0026 §Estado /
 * TanStack Query). Toda leitura/escrita de servidor passa pelo Query: cache,
 * dedup e revalidação com a config conservadora do `criarQueryClient`.
 *
 * Convenção de queryKey: prefixo `"receitas"` + recurso. A mutação NÃO tem
 * cache própria (é uma escrita); no SUCESSO ela INVALIDA as queries que o
 * lançamento afeta — projeção e dashboard da empresa.
 */

/** Empresas sobre as quais o usuário pode lançar receita manual (escopo do token). */
export function useEmpresasReceita(): UseQueryResult<EmpresaReceita[]> {
  return useQuery({
    queryKey: ["receitas", "empresas"],
    queryFn: buscarEmpresasReceita,
  });
}

/**
 * Lança uma receita manual (ESCRITA de money) da empresa fixada no hook.
 *
 * `empresaId` é fixado no hook (a tela já sabe a empresa em foco); a mutação
 * recebe os `ReceitaManualIn` no `mutate`. NO SUCESSO, INVALIDA as queries que o
 * lançamento afeta — uma receita futura/de hoje passa a SOMAR na projeção e nos
 * KPIs do dashboard daquela empresa:
 *
 *  - `["projecao","empresa",empresaId]` — a série diária da empresa;
 *  - `["projecao","composicao",empresaId]` — o drill-down itemizado;
 *  - `["dashboard",empresaId]` — os KPIs da empresa.
 *
 * As chaves são PARCIAIS (prefixo): o `invalidateQueries` casa TODOS os horizontes
 * já cacheados daquela empresa (a queryKey real inclui o horizonte ao final).
 *
 * NÃO seta `retry`: `criarQueryClient` já aplica `mutations:{retry:0}` — lançar
 * dinheiro NÃO retenta às cegas (evita lançamento duplicado por retry).
 */
export function useCriarReceitaManual(
  empresaId: string,
): UseMutationResult<ReceitaManualOut, unknown, ReceitaManualIn> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dados: ReceitaManualIn) =>
      criarReceitaManual(empresaId, dados),
    onSuccess: () => {
      // Chaves PARCIAIS (prefixo) — casam todos os horizontes cacheados da empresa.
      queryClient.invalidateQueries({
        queryKey: ["projecao", "empresa", empresaId],
      });
      queryClient.invalidateQueries({
        queryKey: ["projecao", "composicao", empresaId],
      });
      queryClient.invalidateQueries({ queryKey: ["dashboard", empresaId] });
    },
  });
}
